import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
// import { Controller } from 'react-hook-form';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import ExternalPolicyVerification from '@/components/PersonInsurancePolicy/ExternalPolicyVerification';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import useCreateInsuranceFromDiscoveredPolicy from '@/hooks/useCreateInsuranceFromDiscoveredPolicy';
import useInsurancePriorityTypes from '@/hooks/useInsurancePriorityTypes';
import { ExternalPolicyVerification as ExternalPolicyVerificationType } from '@/types/externalVerification';
// import { currencyDollarsToCentsFormatter, parseStringToDollars } from '@/utils/currencies';
import { Banner } from '@/ui';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import DataList, { DataRow } from '@/ui/components/DataList';
// import InputField from '@/ui/components/Form/InputField';
import RadioGroup from '@/ui/components/Form/RadioGroup';
import { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import PersonContext from '../Person/PersonContext';

const NewInsuranceFromDiscovered = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { insuranceId } = useParams<{ insuranceId: string }>();
  const externalPolicyVerification =
    state?.externalPolicyVerification as ExternalPolicyVerificationType;
  const {
    carrier,
    type,
    policyholder_dob,
    policyholder_first_name,
    policyholder_last_name,
    plan,
    subscriber_id
  } = externalPolicyVerification.potential_insurance_policy_attrs || {};
  const { updatePerson } = useContext(PersonContext);

  const [priorityTypesOptions, setPriorityTypesOptions] = useState<OptionType<string>[] | null>();

  // TODO: Uncomment when AT-6328 is done
  // const insurancePriorityFromNavigationState = state?.insurancePolicyPriority;
  // const [insurancePriority, setInsurancePriority] = useState<string | null>(
  //   insurancePriorityFromNavigationState
  // );

  const [insurancePriority, setInsurancePriority] = useState('Unknown');

  const { data: priorityTypes } = useInsurancePriorityTypes();

  const { mutateAsync: createInsurance, isPending: isCreatingInsurance } =
    useCreateInsuranceFromDiscoveredPolicy();
  const { addMessage } = useContext(AppContext);

  const [isUpdatingPerson, setIsUpdatingPerson] = useState(false);

  useEffect(() => {
    if (priorityTypes?.data) {
      const formattedOptions = priorityTypes.data.map((priorityType) => ({
        label: priorityType,
        value: priorityType
      }));
      setPriorityTypesOptions(formattedOptions);
    }
  }, [priorityTypes]);

  const saveNewInsurance = async () => {
    try {
      // TODO: This needs to input the values from estimated costs
      await createInsurance({
        external_policy_verification_id: externalPolicyVerification.id,
        insurance_policy_priority_status: insurancePriority,
        insurance_policy_to_end_id: insuranceId,
        mutable_fields: {}
      });
      setIsUpdatingPerson(true);
      await updatePerson();
      addMessage({ level: 'success', text: 'Insurance created successfully' });
      setIsUpdatingPerson(false);
      navigate(-1);
    } catch (error) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  };

  return (
    <Container type="page">
      <div className="inline-flex items-center">
        <button
          className="w-6 inline-block text-center mb-1"
          onClick={() => navigate(-1)}
          type="button">
          <FontAwesomeIcon icon={faChevronLeft} className="mr-1 text-xl" />
        </button>
        <PageHeader headingText="New insurance from discovered policy" />
      </div>
      <Card className="flex flex-col gap-2">
        <p className="mb-2">
          Create a new insurance policy to attach the discovered verification to
        </p>
        <Heading value={3} text="Discovered verification" />
        <div className="relative border-solid border border-gray-200 rounded-lg p-2 mb-2">
          <ExternalPolicyVerification
            externalPolicyVerification={externalPolicyVerification}
            debugObject={externalPolicyVerification}
          />
        </div>
        <Heading value={3} text="New Policy" />
        <div className="relative border-solid border border-gray-200 rounded-lg p-2">
          <DataList className="[&>div]:flex [&>div]:flex-row [&>div]:gap-2 mb-2">
            <DataRow label="Carrier:">{carrier || '-'}</DataRow>
            <DataRow label="Plan:">{plan || '-'}</DataRow>
            <DataRow label="Subscriber ID:" sensitive>
              {subscriber_id}
            </DataRow>
            <DataRow label="Type:">{type || '-'}</DataRow>
            {priorityTypesOptions && (
              <DataRow label="Order:">
                <RadioGroup
                  id="priority"
                  options={priorityTypesOptions}
                  selectedValue={insurancePriority || ''}
                  onChange={(option) => setInsurancePriority(option as string)}
                  // TODO: Uncomment when AT-6328 is done
                  disabled
                />
              </DataRow>
            )}
            {/* <Controller
              name="estimated_monthly_cost"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <InputField
                    label="Estimated Monthly Cost"
                    type="text"
                    inputMode="decimal"
                    requiredField={true}
                    id="estimated_monthly_cost"
                    placeholder="$0.00"
                    step={0.01}
                    min={0}
                    onChange={(e) => {
                      const inputVal = e.target.value.trim();
                      onChange(inputVal === '' ? null : parseStringToDollars(inputVal));
                    }}
                    value={value === null ? '' : String(value)}
                  />
                  {error && <span className="text-red-500 text-sm">{error.message}</span>}
                </>
              )}
            />
            <Controller
              name="estimated_initial_month_cost"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <InputField
                    label="Estimated Initial Month Cost"
                    type="text"
                    inputMode="decimal"
                    requiredField={true}
                    id="estimated_initial_month_cost"
                    placeholder="$0.00"
                    step={0.01}
                    min={0}
                    onChange={(e) => {
                      const inputVal = e.target.value.trim();
                      onChange(inputVal === '' ? null : parseStringToDollars(inputVal));
                    }}
                    value={value === null ? '' : String(value)}
                  />
                  {error && <span className="text-red-500 text-sm">{error.message}</span>}
                </>
              )}
            /> */}
          </DataList>
          <Heading value={4} text="Policyholder" />
          <hr />
          <DataList className="[&>div]:flex [&>div]:flex-row [&>div]:gap-2 mt-1">
            <DataRow label="First name:" sensitive>
              {policyholder_first_name || '-'}
            </DataRow>
            <DataRow label="Last name:" sensitive>
              {policyholder_last_name || '-'}
            </DataRow>
            <DataRow label="DOB:" sensitive>
              {policyholder_dob || '-'}
            </DataRow>
          </DataList>
        </div>
        <Banner
          color="amber"
          label="Saving this policy will set the end date on the current policy to today"
        />
        <Button
          ariaLabel="Save new policy"
          size="sm"
          color={Color.WHITE}
          onClick={saveNewInsurance}
          className="mt-2 w-full"
          disabled={isCreatingInsurance || isUpdatingPerson}
          loading={isCreatingInsurance || isUpdatingPerson}>
          Save new policy
        </Button>
      </Card>
    </Container>
  );
};

export default NewInsuranceFromDiscovered;
